import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TPeriodDays } from '@src/app/shared/types/common';
import { isPlatformBrowser } from '@angular/common';
import { SourceLoaderService } from './source-loader.service';
import { FormGroup } from '@angular/forms';
import { FormatDatePipe } from '@src/app/pipes';
import { currencySymbols } from '@src/app/constants/currency-symbols';
import { calculateDateRange, parseDotDateToISO } from '@src/app/utils';
import { commonFormat } from '@src/app/constants/date';

export type TCurrency = Record<string, any>;

interface IMinAndMaxDatesParams {
  classInstance?: any;
  form: FormGroup<any>;
}

interface ICurrencyChangeHandlerParams extends Partial<IMinAndMaxDatesParams> {
  activeCurrency: TCurrency;
  selectedCurrency: TCurrency;
  onSetActiveCurrency: (val: TCurrency) => void;
  onSetCurrencySymbol: (symbol: string) => void;
  onSetFormInitialValues: (currency: TCurrency) => void;
  callbackFn?: () => void;
  getStartDate?: () => any;
  calcPeriodDays?: (params: {
    rates: Array<Record<string, string | number>>;
  }) => TPeriodDays;
  onSetPeriodDays?: (days: any) => void;
}

interface IValueRecalculationParams extends IMinAndMaxDatesParams {
  updatingInternally: boolean;
  callbackFn?: () => void;
}

interface IFormInitalValuesParams extends IMinAndMaxDatesParams {
  periodDays: TPeriodDays;
  amount: number;
}

@Injectable({
  providedIn: 'root',
})
export class CalculatorUtilsService {
  private formatDate = new FormatDatePipe();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private scriptLoader: SourceLoaderService,
  ) {}

  initLoanCalculatorLibs({
    sources,
    onSuccess,
    onError,
  }: {
    sources: string[];
    onSuccess?: () => void;
    onError?: () => void;
  }): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.scriptLoader.loadScript(sources).subscribe({
      next: () => {
        onSuccess && onSuccess();
      },
      error: (err: string) => {
        onError && onError();

        console.error(err);
      },
    });
  }

  handleCalcCurrencyChange = (params: ICurrencyChangeHandlerParams): void => {
    const {
      classInstance,
      activeCurrency,
      selectedCurrency,
      onSetActiveCurrency,
      onSetPeriodDays,
      onSetCurrencySymbol,
      onSetFormInitialValues,
      form,
      getStartDate,
      calcPeriodDays,
      callbackFn,
    } = params;

    if (activeCurrency?.['currency'] === selectedCurrency?.['currency']) return;

    onSetActiveCurrency(selectedCurrency);

    const periodDays = calcPeriodDays?.({
      rates: selectedCurrency['rates'],
    });
    onSetPeriodDays?.(periodDays);

    const symbol =
      currencySymbols[
        selectedCurrency['currency'] as keyof typeof currencySymbols
      ];
    onSetCurrencySymbol(symbol);

    onSetFormInitialValues(selectedCurrency);

    form &&
      classInstance &&
      this.setMinAndMaxDates({
        startDate: getStartDate && getStartDate(),
        classInstance,
        form,
      });

    callbackFn?.();
  };

  private setMinAndMaxDates({
    startDate,
    form,
    classInstance,
  }: IMinAndMaxDatesParams & {
    startDate?: any;
  }): void {
    classInstance['endDateMin'] = startDate;
    classInstance['endDateMax'] = this.formatDate.transform({
      value: calculateDateRange({
        startDate: new Date(parseDotDateToISO(startDate)),
        days: form.get('days')?.value,
      }).endDate!,
      format: commonFormat,
    });
  }

  setFormInitalValues(params: IFormInitalValuesParams): void {
    const { form, amount, classInstance, periodDays } = params;

    const defaultStartDate = this.formatDate.transform({
      value: new Date(),
      format: commonFormat,
    });

    const endDateByDays = this.formatDate.transform({
      value: calculateDateRange({
        startDate: new Date(),
        days: periodDays.max,
      }).endDate!,
      format: commonFormat,
    });

    classInstance['endDateByDays'] = endDateByDays;

    form.patchValue({
      amount,
      days: periodDays.min,
      startDate: defaultStartDate,
      endDate: this.formatDate.transform({
        value: calculateDateRange({
          startDate: new Date(),
          days: periodDays.min,
        }).endDate!,
        format: commonFormat,
      }),
    });

    this.setMinAndMaxDates({
      startDate: defaultStartDate,
      classInstance,
      form,
    });
  }

  recalculateEndDate(params: IValueRecalculationParams): void {
    const { form, callbackFn, classInstance } = params;

    if (params.updatingInternally) return;

    const startDate = form.get('startDate')?.value;
    const days = Number(form.get('days')?.value);

    if (!startDate || isNaN(days)) return;

    const currentEndDate = form.get('endDate')?.value;
    const newEndDate = this.formatDate.transform({
      value: calculateDateRange({
        startDate: new Date(parseDotDateToISO(startDate)),
        days,
      }).endDate!,
      format: commonFormat,
    });

    if (currentEndDate !== newEndDate) {
      params.updatingInternally = true;

      form.patchValue({
        endDate: newEndDate,
      });

      params.updatingInternally = false;

      this.setMinAndMaxDates({
        classInstance,
        startDate: startDate,
        form,
      });
    }

    callbackFn?.();
  }

  recalculateDays(params: IValueRecalculationParams): void {
    const { form, classInstance, callbackFn } = params;

    if (params.updatingInternally) return;

    const startDate = form.get('startDate')?.value;
    const endDate = form.get('endDate')?.value;

    if (!startDate || !endDate) return;

    const currentDays = Number(form.get('days')?.value);
    const daysDiff = calculateDateRange({
      startDate: new Date(parseDotDateToISO(startDate)),
      endDate: new Date(parseDotDateToISO(endDate)),
    }).days;

    if (daysDiff !== currentDays) {
      params.updatingInternally = true;

      form.patchValue({
        days: daysDiff,
      });

      params.updatingInternally = false;

      this.setMinAndMaxDates({
        startDate: startDate,
        classInstance,
        form,
      });
    }

    callbackFn?.();
  }
}
